import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHead } from "@/pages/admin/AdminLayout";
import { RideAdminCard } from "@/pages/admin/RideAdminCard";
import { ActiveRideCard } from "@/components/ActiveRideCard";
import { Button } from "@/components/ui/button";

const Tile = ({ label, value, to, testid, hot }) => (
  <Link to={to} data-testid={testid} className={`rounded-xl border p-4 transition-shadow hover:shadow-[0_0_18px_hsl(var(--neon-violet)/0.3)] ${hot ? "border-primary bg-primary text-primary-foreground" : "bg-card"}`}>
    <p className={`text-[11px] font-semibold uppercase tracking-wider ${hot ? "text-black/70" : "text-muted-foreground"}`}>{label}</p>
    <p className="mt-1 font-display text-3xl font-bold">{value}</p>
  </Link>
);

const Section = ({ title, rides, empty, testid }) => (
  <section className="space-y-3" data-testid={testid}>
    <h2 className="font-display text-lg font-semibold">{title} <span className="text-muted-foreground">({rides.length})</span></h2>
    {!rides.length && <p className="rounded-xl border border-dashed p-4 text-sm text-muted-foreground">{empty}</p>}
    <div className="grid gap-3 lg:grid-cols-2">{rides.map((r) => <RideAdminCard key={r.id} ride={r} />)}</div>
  </section>
);

export default function AdminDashboard() {
  const { user } = useAuth();
  const { data } = useQuery({ queryKey: ["adminDashboard"], queryFn: () => api.get("/admin/dashboard").then((r) => r.data), refetchInterval: 8000 });
  const { data: bill } = useQuery({ queryKey: ["billingSummary"], queryFn: () => api.get("/admin/billing/summary").then((r) => r.data) });
  const { data: myActive } = useQuery({ queryKey: ["driverActive"], queryFn: () => api.get("/driver/active").then((r) => r.data),
    refetchInterval: 5000, enabled: !!user?.modes?.includes("driver") });
  if (!data) return null;
  const c = data.counts;
  const todayOnly = data.today.filter((r) => r.status !== "pending" && r.id !== myActive?.id);
  return (
    <div className="fade-up space-y-8" data-testid="admin-dashboard">
      <PageHead title="Dispatch" sub="Approve, assign, drive — all from here.">
        <Button asChild data-testid="quick-book-button"><Link to="/admin/book"><Plus className="mr-1 h-4 w-4" /> Quick book</Link></Button>
        <Button asChild variant="outline" data-testid="add-ride-button"><Link to="/admin/book?mode=add"><Plus className="mr-1 h-4 w-4" /> Add ride</Link></Button>
        <Button asChild variant="outline" data-testid="add-user-button"><Link to="/admin/users?add=1"><Plus className="mr-1 h-4 w-4" /> Add user</Link></Button>
        <Button asChild variant="outline" data-testid="add-location-quick-button"><Link to="/admin/directory?add=1"><Plus className="mr-1 h-4 w-4" /> Add location</Link></Button>
      </PageHead>
      {bill && (
        <Link to="/admin/payments" className="grid grid-cols-2 gap-2 rounded-xl border bg-card p-3 text-sm hover:border-primary/50 md:grid-cols-4" data-testid="billing-overview">
          <span>Post-payment due <b data-testid="billing-due">${bill.post_payment.due.amount.toFixed(2)}</b></span>
          <span className={bill.post_payment.overdue.count ? "text-destructive" : ""}>Overdue <b data-testid="billing-overdue">${bill.post_payment.overdue.amount.toFixed(2)}</b></span>
          <span>Applications <b data-testid="billing-applications">{bill.post_payment.pending_applications}</b></span>
          <span>Rider credit <b data-testid="billing-credit">${bill.credit.outstanding.toFixed(2)}</b>{bill.pending_purchases ? ` · ${bill.pending_purchases} to confirm` : ""}</span>
        </Link>
      )}
      {data.fuel_review?.is_due && (
        <Link to="/admin/pricing" className="block rounded-xl border border-amber-400/50 bg-amber-400/10 px-4 py-3 text-sm text-amber-100 hover:bg-amber-400/15" data-testid="fuel-review-due-banner">
          Fuel-price review is due (every {data.fuel_review.days} days). Compare rates against current fuel prices in Pricing →
        </Link>
      )}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-8">
        <Tile label="Pending" value={c.pending} to="/admin" testid="count-pending" hot={c.pending > 0} />
        <Tile label="Today's rides" value={c.today} to="/admin/rides?range=today" testid="count-today" />
        <Tile label="Active ride" value={c.active} to="/admin" testid="count-active" />
        <Tile label="Upcoming" value={c.upcoming} to="/admin/rides" testid="count-upcoming" />
        <Tile label="Driver apps" value={c.driver_applications} to="/admin/drivers" testid="count-driver-apps" />
        <Tile label="Users" value={c.users} to="/admin/users" testid="count-users" />
        <Tile label="Directory" value={c.locations} to="/admin/directory" testid="count-locations" />
        <Tile label="Pricing" value={`v${c.pricing_version}`} to="/admin/pricing" testid="count-pricing" />
      </div>
      <Section title="Pending requests" rides={data.pending} empty="No pending requests." testid="pending-section" />
      {myActive && (
        <section className="space-y-3 lg:max-w-xl" data-testid="my-active-ride-section">
          <h2 className="font-display text-lg font-semibold">My active ride</h2>
          <ActiveRideCard ride={myActive} />
        </section>
      )}
      <Section title="Today's rides" rides={todayOnly} empty="Nothing else scheduled today." testid="today-section" />
      <Section title="Upcoming" rides={data.upcoming} empty="No upcoming confirmed rides." testid="upcoming-section" />
    </div>
  );
}
